/**
 * WidgetSettings — настройки кастомизации виджета.
 *
 * Color picker, text inputs, toggles, position selector.
 * Widget v2 Step 4.
 */

import { useState, useEffect, useCallback } from 'react';
import { Button, Card } from '../ui';
import { useUpdateWidgetConfig, type WidgetConfig, type WidgetConfigUpdate } from '../../api/channels';

// ============================================================================
// Types
// ============================================================================

interface WidgetSettingsProps {
  config: WidgetConfig;
}

function configToForm(config: WidgetConfig): WidgetConfigUpdate {
  return {
    name: config.name,
    color: config.color,
    subtitle: config.subtitle ?? '',
    greeting: config.greeting ?? '',
    placeholder: config.placeholder ?? '',
    botName: config.botName ?? '',
    botAvatarUrl: config.botAvatarUrl ?? '',
    position: config.position,
    branding: config.branding,
    enabled: config.enabled,
  };
}

// ============================================================================
// Component
// ============================================================================

export function WidgetSettings({ config }: WidgetSettingsProps): JSX.Element {
  const updateConfig = useUpdateWidgetConfig();

  const [form, setForm] = useState<WidgetConfigUpdate>(() => configToForm(config));
  const [dirty, setDirty] = useState(false);

  // Синхронизация при обновлении config из сервера
  useEffect(() => {
    setForm(configToForm(config));
    setDirty(false);
  }, [config]);

  const updateField = useCallback(<K extends keyof WidgetConfigUpdate>(key: K, value: WidgetConfigUpdate[K]) => {
    setForm(prev => ({ ...prev, [key]: value }));
    setDirty(true);
  }, []);

  const isColorValid = /^#[0-9A-Fa-f]{6}$/.test(form.color ?? '#4F46E5');

  const handleSave = () => {
    if (!isColorValid) return;
    updateConfig.mutate(form, {
      onSuccess: () => setDirty(false),
    });
  };

  return (
    <Card className="border border-border-subtle">
      <div className="flex items-center justify-between mb-6">
        <h3 className="font-heading text-lg font-semibold text-text-primary">Настройки виджета</h3>
        <div className="flex items-center gap-3">
          {dirty && (
            <span className="text-xs text-text-muted">Есть несохранённые изменения</span>
          )}
          <Button
            variant="primary"
            size="sm"
            onClick={handleSave}
            disabled={!dirty || updateConfig.isPending || !isColorValid}
          >
            {updateConfig.isPending ? 'Сохранение...' : 'Сохранить'}
          </Button>
        </div>
      </div>

      <div className="space-y-5">
        {/* Вкл/Выкл */}
        <ToggleField
          label="Виджет активен"
          description="Если выключен, виджет не будет отображаться на сайте"
          checked={form.enabled ?? true}
          onChange={(v) => updateField('enabled', v)}
        />

        <hr className="border-border-subtle" />

        {/* Основные тексты */}
        <TextField
          label="Название"
          value={form.name ?? ''}
          onChange={(v) => updateField('name', v)}
          placeholder="Anima"
        />
        <TextField
          label="Имя бота"
          value={form.botName ?? ''}
          onChange={(v) => updateField('botName', v)}
          placeholder="Anima"
        />
        <TextField
          label="Аватар бота (URL)"
          value={form.botAvatarUrl ?? ''}
          onChange={(v) => updateField('botAvatarUrl', v)}
          placeholder="https://example.com/avatar.png"
        />
        <TextField
          label="Подзаголовок"
          value={form.subtitle ?? ''}
          onChange={(v) => updateField('subtitle', v)}
          placeholder="Онлайн · обычно отвечаем за пару минут"
        />
        <TextAreaField
          label="Приветствие"
          value={form.greeting ?? ''}
          onChange={(v) => updateField('greeting', v)}
          placeholder="Здравствуйте! Чем могу помочь?"
        />
        <TextField
          label="Placeholder в поле ввода"
          value={form.placeholder ?? ''}
          onChange={(v) => updateField('placeholder', v)}
          placeholder="Напишите сообщение…"
        />

        <hr className="border-border-subtle" />

        {/* Цвет */}
        <ColorField
          label="Основной цвет"
          value={form.color ?? '#4F46E5'}
          onChange={(v) => updateField('color', v)}
        />

        {/* Позиция */}
        <PositionField
          value={form.position ?? 'bottom-right'}
          onChange={(v) => updateField('position', v)}
        />

        {/* Branding */}
        <ToggleField
          label="Показывать Powered by Anima"
          description="Ссылка в нижней части виджета"
          checked={form.branding ?? true}
          onChange={(v) => updateField('branding', v)}
        />
      </div>

      {/* Превью цвета */}
      <div className="mt-6 p-4 bg-surface-subtle rounded-comp">
        <p className="text-xs text-text-muted mb-2">Превью</p>
        <div className="flex items-center gap-3">
          <div
            className="w-12 h-12 rounded-full flex items-center justify-center text-white shadow-md"
            style={{ background: `linear-gradient(135deg, ${form.color ?? '#4F46E5'}, ${form.color ?? '#4F46E5'}dd)` }}
          >
            <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
              <path d="M12 2l2.4 7.6L22 12l-7.6 2.4L12 22l-2.4-7.6L2 12l7.6-2.4L12 2z" />
            </svg>
          </div>
          <div>
            <p className="text-sm font-medium text-text-primary">{form.name || 'Anima'}</p>
            <p className="text-xs text-text-secondary">{form.subtitle || 'Онлайн · обычно отвечаем за пару минут'}</p>
          </div>
        </div>
      </div>
    </Card>
  );
}

// ============================================================================
// Field components
// ============================================================================

function TextField({ label, value, onChange, placeholder }: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}): JSX.Element {
  return (
    <div>
      <label className="block text-sm font-medium text-text-primary mb-1.5">{label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-3 py-2 rounded-comp border border-border-subtle bg-surface-base text-text-primary text-sm
                   focus:outline-none focus:ring-2 focus:ring-accent-brand/30 focus:border-accent-brand transition-colors"
      />
    </div>
  );
}

function TextAreaField({ label, value, onChange, placeholder }: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}): JSX.Element {
  return (
    <div>
      <label className="block text-sm font-medium text-text-primary mb-1.5">{label}</label>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={2}
        className="w-full px-3 py-2 rounded-comp border border-border-subtle bg-surface-base text-text-primary text-sm
                   resize-none focus:outline-none focus:ring-2 focus:ring-accent-brand/30 focus:border-accent-brand transition-colors"
      />
    </div>
  );
}

function ColorField({ label, value, onChange }: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}): JSX.Element {
  const presets = ['#4F46E5', '#2563EB', '#0891B2', '#059669', '#D97706', '#DC2626', '#7C3AED', '#DB2777'];

  return (
    <div>
      <label className="block text-sm font-medium text-text-primary mb-1.5">{label}</label>
      <div className="flex items-center gap-3">
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-10 h-10 rounded-comp border border-border-subtle cursor-pointer bg-transparent p-0.5"
        />
        <input
          type="text"
          value={value}
          onChange={(e) => {
            const v = e.target.value;
            if (/^#[0-9A-Fa-f]{0,6}$/.test(v)) onChange(v);
          }}
          className="w-24 px-3 py-2 rounded-comp border border-border-subtle bg-surface-base text-text-primary text-sm font-mono
                     focus:outline-none focus:ring-2 focus:ring-accent-brand/30 focus:border-accent-brand"
          maxLength={7}
        />
        <div className="flex gap-1.5">
          {presets.map((c) => (
            <button
              key={c}
              onClick={() => onChange(c)}
              className={`w-6 h-6 rounded-full border-2 transition-transform hover:scale-110 ${
                value === c ? 'border-text-primary scale-110' : 'border-transparent'
              }`}
              style={{ backgroundColor: c }}
              title={c}
              aria-label={`Цвет ${c}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function PositionField({ value, onChange }: {
  value: 'bottom-right' | 'bottom-left';
  onChange: (v: 'bottom-right' | 'bottom-left') => void;
}): JSX.Element {
  return (
    <div>
      <label className="block text-sm font-medium text-text-primary mb-1.5">Позиция на странице</label>
      <div className="flex gap-2">
        <button
          onClick={() => onChange('bottom-left')}
          className={`flex-1 px-3 py-2 rounded-comp border text-sm transition-colors ${
            value === 'bottom-left'
              ? 'border-accent-brand bg-accent-brand/5 text-accent-brand font-medium'
              : 'border-border-subtle text-text-secondary hover:border-border-default'
          }`}
        >
          ↙ Слева внизу
        </button>
        <button
          onClick={() => onChange('bottom-right')}
          className={`flex-1 px-3 py-2 rounded-comp border text-sm transition-colors ${
            value === 'bottom-right'
              ? 'border-accent-brand bg-accent-brand/5 text-accent-brand font-medium'
              : 'border-border-subtle text-text-secondary hover:border-border-default'
          }`}
        >
          Справа внизу ↘
        </button>
      </div>
    </div>
  );
}

function ToggleField({ label, description, checked, onChange }: {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}): JSX.Element {
  return (
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm font-medium text-text-primary">{label}</p>
        {description && <p className="text-xs text-text-muted mt-0.5">{description}</p>}
      </div>
      <button
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`relative w-11 h-6 rounded-full transition-colors ${
          checked ? 'bg-accent-brand' : 'bg-surface-subtle border border-border-subtle'
        }`}
      >
        <span
          className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${
            checked ? 'translate-x-5' : 'translate-x-0'
          }`}
        />
      </button>
    </div>
  );
}
